import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Home, History, Settings, User, Phone, Mail, Key, Zap, CheckCircle, XCircle } from "lucide-react";

export default function OneXStavkaCombinedPreview() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [browserTheme, setBrowserTheme] = useState('dark');
  const [currentPage, setCurrentPage] = useState('main');

  useEffect(() => {
    const dataParam = searchParams.get('data');
    const themeParam = searchParams.get('theme');
    if (themeParam) setBrowserTheme(themeParam);
    if (dataParam) { try { setData(JSON.parse(decodeURIComponent(dataParam))); } catch (e) { console.error(e); } }
  }, [searchParams]);

  useEffect(() => {
    const titles = { main: 'Live', history: 'Мои ставки', settings: 'Профиль' };
    document.title = `${titles[currentPage]} | 1xСтавка`;
    let favicon = document.querySelector("link[rel='icon']");
    if (!favicon) { favicon = document.createElement('link'); favicon.rel = 'icon'; document.head.appendChild(favicon); }
    favicon.href = '/1xstavka-favicon.svg';
  }, [currentPage]);

  if (!data) return <div className="min-h-screen bg-blue-900 flex items-center justify-center text-white">Загрузка...</div>;

  const isDark = browserTheme === 'dark';
  const theme = { bg: isDark ? '#202124' : '#dee1e6', activeTabBg: isDark ? '#292a2d' : '#ffffff', inactiveTabBg: isDark ? '#35363a' : '#c9cdd4', textPrimary: isDark ? '#e8eaed' : '#202124', textSecondary: isDark ? '#9aa0a6' : '#5f6368' };
  const accountName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Пользователь';
  const userId = data.visibleId || '00000000';
  const totalBalance = data.totalBalance || (parseFloat(data.balance || 0) + parseFloat(data.bonusBalance || 0));

  const BrowserFrame = () => (
    <div style={{ backgroundColor: theme.bg }} className="flex-shrink-0">
      <div className="flex items-center h-[38px] pl-2">
        <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] mr-[1px]" style={{ backgroundColor: theme.inactiveTabBg }}><img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2" /><span className="text-[13px]" style={{ color: theme.textSecondary }}>Ozon Карта</span></div>
        <div className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[180px]" style={{ backgroundColor: theme.activeTabBg }}><img src="/1xstavka-favicon.svg" alt="" className="w-4 h-4 mr-2" /><span className="text-[13px]" style={{ color: theme.textPrimary }}>1xСтавка</span></div>
      </div>
      <div className="flex items-center h-[40px] px-4 gap-4" style={{ backgroundColor: theme.activeTabBg }}>
        <div className="flex gap-2">{['←', '→', '↻'].map((a, i) => <button key={i} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ color: theme.textSecondary }}>{a}</button>)}</div>
        <div className="flex-1 h-[32px] rounded-full px-4 flex items-center" style={{ backgroundColor: isDark ? '#202124' : '#f1f3f4' }}><span className="text-[14px]" style={{ color: theme.textSecondary }}>🔒 1xstavka.ru/account/{userId}</span></div>
      </div>
    </div>
  );

  const MainPage = () => (
    <div className="p-6">
      <div className="bg-gradient-to-br from-blue-600 to-cyan-500 rounded-2xl p-6 text-white mb-6">
        <div className="text-sm opacity-80 mb-1">Баланс</div>
        <div className="text-3xl font-bold">{parseFloat(totalBalance).toLocaleString('ru-RU')} ₽</div>
        <div className="flex gap-4 mt-3 text-sm">
          <div>Основной: {parseFloat(data.balance || 0).toLocaleString()} ₽</div>
          <div className="text-yellow-300">Бонус: {parseFloat(data.bonusBalance || 0).toLocaleString()} ₽</div>
        </div>
        <div className="flex items-center gap-2 mt-3"><Zap className="w-4 h-4 text-yellow-400" /><span>Фрибеты: {data.freebets || 0}</span></div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-6">
        <button className="bg-blue-600 text-white rounded-xl p-4 font-medium hover:bg-blue-700 transition-colors">Пополнить</button>
        <button className="bg-white text-gray-700 rounded-xl p-4 font-medium hover:bg-gray-50 transition-colors border">Вывести</button>
      </div>
      <h3 className="font-medium text-gray-800 mb-3">Live события</h3>
      <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
        {['Футбол: ЦСКА - Спартак', 'Хоккей: СКА - ЦСКА', 'Теннис: Медведев - Джокович'].map((e, i) => (
          <div key={i} className="p-4 flex items-center justify-between"><span className="text-gray-700">{e}</span><div className="flex gap-2">{[1, 2, 3].map(n => <span key={n} className="bg-blue-50 text-blue-700 px-3 py-1 rounded font-medium text-sm">{(1 + Math.random() * 3).toFixed(2)}</span>)}</div></div>
        ))}
      </div>
    </div>
  );

  const HistoryPage = () => (
    <div className="p-6">
      <h2 className="text-xl font-bold text-gray-800 mb-4">Мои ставки</h2>
      <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
        {(data.bets || []).map((bet, i) => (
          <div key={i} className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${bet.type === 'win' ? 'bg-green-100' : 'bg-red-100'}`}>
                {bet.type === 'win' ? <CheckCircle className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
              </div>
              <div><div className="font-medium text-gray-800">{bet.sport}: {bet.event}</div><div className="text-sm text-gray-500">{bet.date} • Коэф. {bet.odds}</div></div>
            </div>
            <div className={`font-medium ${bet.type === 'win' ? 'text-green-600' : 'text-red-600'}`}>{bet.type === 'win' ? '+' : '-'}{bet.amount} ₽</div>
          </div>
        ))}
        {(!data.bets || data.bets.length === 0) && <div className="p-8 text-center text-gray-500">Нет ставок</div>}
      </div>
    </div>
  );

  const SettingsPage = () => (
    <div className="p-6">
      <h2 className="text-xl font-bold text-gray-800 mb-4">Профиль</h2>
      <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
        {[{ icon: User, label: 'Имя', value: accountName }, { icon: Mail, label: 'Email', value: data.email }, { icon: Phone, label: 'Телефон', value: data.phone }, { icon: Key, label: 'ID', value: userId }].map((item, i) => (
          <div key={i} className="p-4 flex items-center justify-between"><div className="flex items-center gap-3"><item.icon className="w-5 h-5 text-gray-400" /><span>{item.label}</span></div><span>{item.value}</span></div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen template-preview-scale" style={{ backgroundColor: theme.bg }}>
      <BrowserFrame />
      <div className="flex" style={{ minHeight: 'calc(100vh - 78px)' }}>
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-100"><div className="flex items-center gap-3"><img src="/1xstavka-logo.svg" alt="" className="w-10 h-10" /><div><div className="font-bold text-gray-800">1xСтавка</div><div className="text-xs text-gray-500">Букмекер</div></div></div></div>
          <div className="flex-1 py-2">
            {[{ id: 'main', icon: Home, label: 'Live' }, { id: 'history', icon: History, label: 'Мои ставки' }, { id: 'settings', icon: Settings, label: 'Профиль' }].map((item) => (
              <button key={item.id} onClick={() => setCurrentPage(item.id)} className={`w-full px-4 py-3 flex items-center gap-3 ${currentPage === item.id ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-500' : 'text-gray-600 hover:bg-gray-50'}`}><item.icon className="w-5 h-5" /><span>{item.label}</span></button>
            ))}
          </div>
          <div className="p-4 border-t border-gray-100"><div className="flex items-center gap-3"><div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center"><span className="text-blue-600 font-bold">{accountName.charAt(0)}</span></div><div><div className="font-medium truncate">{accountName}</div><div className="text-xs text-gray-500">ID: {userId}</div></div></div></div>
        </div>
        <div className="flex-1 bg-gray-50">{currentPage === 'main' && <MainPage />}{currentPage === 'history' && <HistoryPage />}{currentPage === 'settings' && <SettingsPage />}</div>
      </div>
    </div>
  );
}
